import { z } from 'zod';
import { httpClient } from '../../../shared/api/httpClient';
import { adaptGoogleAuthResponse, type GoogleAuthAdaptation } from '../../../shared/api/intelliSightAdapters';

const googleAuthUrlSchema = z.object({
    authUrl: z.string().url(),
    state: z.string().regex(/^[A-Za-z0-9_-]{43}$/)
});

export type GoogleAuthUrlResponse = z.infer<typeof googleAuthUrlSchema> & { codeVerifier: string };

export interface GoogleCallbackPayload {
    code: string;
    state: string;
    codeVerifier: string;
}

export class GoogleOAuthError extends Error {
    constructor(readonly translationKey: string) {
        super(translationKey);
        this.name = 'GoogleOAuthError';
    }
}

function base64Url(bytes: Uint8Array): string {
    return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * 获取 Google OAuth 授权 URL
 */
export async function getGoogleAuthUrl(): Promise<GoogleAuthUrlResponse> {
    const registrationId = import.meta.env.VITE_GOOGLE_OAUTH_REGISTRATION_ID;
    if (!registrationId) {
        throw new GoogleOAuthError('account.oauth.configurationMissing');
    }
    if (!crypto.subtle) {
        throw new GoogleOAuthError('account.oauth.secureContextRequired');
    }
    const codeVerifier = base64Url(crypto.getRandomValues(new Uint8Array(32)));
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier));
    const query = new URLSearchParams({
        registrationId,
        redirectUri: window.location.origin + '/auth/google/callback',
        codeChallenge: base64Url(new Uint8Array(digest))
    });
    const response = await httpClient.get('/api/v1/auth/google/url?' + query.toString());
    return { ...googleAuthUrlSchema.parse(response), codeVerifier };
}

/**
 * 使用授权码换取用户令牌
 */
export async function exchangeGoogleCode(payload: GoogleCallbackPayload): Promise<GoogleAuthAdaptation> {
    const response = await httpClient.post('/api/v1/auth/google/callback', {
        json: payload
    });
    return adaptGoogleAuthResponse(response);
}
